import { ACKNOWLEDGEMENT_LOOKBACK_MS } from "@storyboard/dispatch-view/acknowledgement";
import type { AssignmentTarget, DispatchAssignmentRow } from "@/store/types";

/** A standing handoff on one target, as the latest row says it. */
export type OpenAssignment = {
  targetKind: "feed_item" | "anchor";
  targetId: string;
  feedId: string;
  assignee: AssignmentTarget;
  note: string | null;
  actorMembershipId: string | null;
  assignedAt: string;
};

/** Every open assignment, by target and by channel. */
export type AssignmentFold = {
  byTarget: ReadonlyMap<string, OpenAssignment>;
  /** Open assignments per feed, newest first. */
  byFeed: ReadonlyMap<string, readonly OpenAssignment[]>;
};

export const NO_ASSIGNMENTS: AssignmentFold = { byTarget: new Map(), byFeed: new Map() };

/** The key {@link AssignmentFold.byTarget} files a target under; feed item and anchor ids never collide across kinds. */
export function assignmentTargetKey(targetKind: "feed_item" | "anchor", targetId: string): string {
  return `${targetKind}:${targetId}`;
}

/** Orders rows as the acknowledgement log does: `(createdAt, id)` ascending, ids compared by code unit. */
function byLogOrder(a: DispatchAssignmentRow, b: DispatchAssignmentRow): number {
  const byInstant = Date.parse(a.createdAt) - Date.parse(b.createdAt);
  if (byInstant !== 0) return byInstant;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

/**
 * Folds the prototype's handoff rows into the assignments that stand.
 *
 * Rows older than the acknowledgement lookback are ignored; per target the latest row by `(createdAt, id)` wins; a
 * winning row with a null `assignee` is no assignment; and an assignment is closed once its target was acknowledged at
 * or after the handoff.
 *
 * @param rows - Every handoff row the store holds, in any order.
 * @param closedAt - The instant of the acknowledgement standing on a target, or `null` when none stands; the caller
 * reads it from the acknowledgement folds, so this never reads the store.
 * @param now - The clock the lookback is measured from, as epoch milliseconds.
 */
export function foldAssignments(
  rows: readonly DispatchAssignmentRow[],
  closedAt: (targetKind: "feed_item" | "anchor", targetId: string) => string | null,
  now: number,
): AssignmentFold {
  const oldest = now - ACKNOWLEDGEMENT_LOOKBACK_MS;
  const latest = new Map<string, DispatchAssignmentRow>();
  for (const row of rows) {
    if (Date.parse(row.createdAt) < oldest) continue;
    const key = assignmentTargetKey(row.targetKind, row.targetId);
    const standing = latest.get(key);
    if (!standing || byLogOrder(standing, row) < 0) latest.set(key, row);
  }

  const byTarget = new Map<string, OpenAssignment>();
  const byFeed = new Map<string, OpenAssignment[]>();
  for (const [key, row] of latest) {
    if (row.assignee === null) continue;
    const closed = closedAt(row.targetKind, row.targetId);
    if (closed !== null && Date.parse(closed) >= Date.parse(row.createdAt)) continue;
    const open: OpenAssignment = {
      targetKind: row.targetKind,
      targetId: row.targetId,
      feedId: row.feedId,
      assignee: row.assignee,
      note: row.note,
      actorMembershipId: row.actorMembershipId,
      assignedAt: row.createdAt,
    };
    byTarget.set(key, open);
    const onFeed = byFeed.get(row.feedId);
    if (onFeed) onFeed.push(open);
    else byFeed.set(row.feedId, [open]);
  }
  for (const onFeed of byFeed.values()) {
    onFeed.sort((a, b) => Date.parse(b.assignedAt) - Date.parse(a.assignedAt));
  }
  return { byTarget, byFeed };
}

/** Whether two assignment targets name the same member, team, or Driver Manager book. */
export function sameAssignmentTarget(a: AssignmentTarget, b: AssignmentTarget): boolean {
  if (a.kind === "member" && b.kind === "member") return a.membershipId === b.membershipId;
  if (a.kind === "team" && b.kind === "team") return a.viewId === b.viewId;
  if (a.kind === "dm" && b.kind === "dm") return a.code === b.code;
  return false;
}
